import type { ChangeEvent, RefObject } from 'react';
import { useCallback, useRef } from 'react';

import { useCampaignTracking } from '#src/app/shared/hooks/campaignTracking';

import { useVideosComponentContext } from '../../../../../../context';
import { useCamera } from '../../../../../../shared/hooks/useCamera';
import { useVideoRecorderActions } from '../../../../../../shared/hooks/useVideoRecorderActions';
import { useIsVideoValid } from '../../../../hooks/useIsVideoValid';

type Result = ModelHookResult<
	{
		inputFileRef: RefObject<HTMLInputElement | null>;
	},
	{
		handleFileUpload: (event: ChangeEvent<HTMLInputElement>) => Promise<void>;
		handleUploadButtonClick: () => void;
	}
>;

export function useChooseVideoButton(): Result {
	const inputFileRef = useRef<HTMLInputElement>(null);
	const { track } = useCampaignTracking();
	const {
		actions: { setErrorMessage },
	} = useVideosComponentContext();

	const { isVideoValid } = useIsVideoValid();
	const { closeCamera } = useCamera();
	const {
		actions: { setBlob, setUploadStep, setVideoSource },
	} = useVideoRecorderActions();

	const handleFileUpload = useCallback(
		async (event: ChangeEvent<HTMLInputElement>) => {
			// added this to make typescript happy, but unsure how to create that scenario
			const files = event.target.files || [];
			const file = files[0];

			const validate = await isVideoValid(file);
			if (!validate.isValid) {
				setErrorMessage(validate.message);
				// This is regular event tracking (not `trackError`) because this is an expected validation
				// error, not a thrown exception.
				track('campaign_action_upload_video_from_device_error', { uploadDurationIfOverLimit: validate.duration });
			} else {
				setBlob(file);
				closeCamera();
				setVideoSource('local');
				setUploadStep('submit');
				setErrorMessage('');
			}
		},
		[isVideoValid, setErrorMessage, setBlob, closeCamera, setUploadStep, setVideoSource, track],
	);

	const handleUploadButtonClick = useCallback(() => {
		track('campaign_action_create_video_choose_file_click');
		if (inputFileRef.current) {
			inputFileRef.current.click();
		}
	}, [track]);

	return {
		data: { inputFileRef },
		actions: {
			handleFileUpload,
			handleUploadButtonClick,
		},
	};
}
